import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, Dialog, H2, Text } from '@betterment/samba-react';
import { ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { TargetAmountAndDate } from './components/TargetAmountAndDate';
import { TargetMonthlyExpenses } from './components/TargetMonthlyExpenses';
import { TargetRetirementSavings } from './components/TargetRetirementSavings';
import { PurposeSettingsDocument } from './graphql/purposeSettings.generated';

import { DeletePurposeConfirmation } from '../../../account/components/settings/components/DeletePurposeConfirmation';
import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { ModalSectionLoading } from '../../../Shared/components-v2/modal-section/components/ModalSectionLoading';
import { ModalSection } from '../../../Shared/components-v2/modal-section/ModalSection';
import { ModalSectionRow } from '../../../Shared/components-v2/modal-section/ModalSectionRow';
import { urlBuilder } from '../../../Shared/urlBuilder';

export const SettingsModalView: React.FC = () => {
  const purposeId = useRequiredParam('purposeId');
  const { data } = useGraphQLQuery(PurposeSettingsDocument, { variables: { id: purposeId }, throwOnError: true });
  const currentPath = window.location.pathname;

  if (!data) {
    return (
      <Box css={{ display: 'grid', gap: 'space.200', mt: 'space.200' }}>
        <ModalSectionLoading />
        <ModalSectionLoading />
      </Box>
    );
  }

  const { purpose } = data;
  const {
    goalId,
    envelope: { accounts },
  } = purpose;

  const editNameUrl = urlBuilder.appGoalsEditProfileName.absolute({ goalId, return_to: currentPath });
  const editPurposeUrl = urlBuilder.appGoalsEditPurpose.absolute({ goal_id: goalId, return_to: currentPath });

  const account = accounts.find(a => a.__typename === 'ManagedAccount' || a.__typename === 'CashAccount');

  const canDeletePurpose = purpose.isDeletionEligible && account;

  return (
    <>
      <IdempotencyProviderWithFeatureRegistry featureName="deletePurpose">
        <ModalSection>
          <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.150' }}>
            <ThemedImage themedAsset={purpose.image} alt="" style={{ width: '36px', height: '36px' }} />
            <Box css={{ display: 'flex', flexDirection: 'column' }}>
              <H2 variant="title200">{purpose.name}</H2>
              <SambaAutoLink to={editNameUrl}>Change name or image</SambaAutoLink>
            </Box>
          </Box>
        </ModalSection>
        <ModalSection header="Plan" cta={<SambaAutoLink to={editPurposeUrl}>Edit</SambaAutoLink>}>
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text color="text">Advice Type</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <Text color="text">{purpose?.adviceTypeName}</Text>
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
          {purpose.target?.__typename === 'TargetAmountAndDate' && <TargetAmountAndDate target={purpose.target} />}
          {purpose.target?.__typename === 'TargetMonthlyExpenses' && <TargetMonthlyExpenses target={purpose.target} />}
          {purpose.target?.__typename === 'TargetRetirementSavings' && (
            <TargetRetirementSavings target={purpose.target} />
          )}
          {purpose.target?.__typename === 'TargetRetirementSpending' && (
            <ModalSectionRow>
              <ModalSectionRow.LeadingContent>
                <Text color="text">Retirement spending</Text>
              </ModalSectionRow.LeadingContent>
              <ModalSectionRow.TrailingContent>
                <Text color="text">{purpose.target.yearsRemaining} years</Text>
              </ModalSectionRow.TrailingContent>
            </ModalSectionRow>
          )}
        </ModalSection>
        {canDeletePurpose && (
          <DeletePurposeConfirmation
            purposeId={purpose.id}
            purposeName={purpose.name}
            accountName={account.name}
            accountId={account.id}
            variant="PurposeSettings"
          />
        )}
      </IdempotencyProviderWithFeatureRegistry>
    </>
  );
};

type Props = {
  showFullHeader?: boolean;
};

export const SettingsModal: React.FC<Props> = ({ showFullHeader = true }) => (
  <Dialog>
    <Dialog.Trigger asChild={true}>
      <Button variant="ghostSecondary" aria-label="Envelope settings">
        <Button.Icon name="Gear" />
        {showFullHeader && 'Settings'}
      </Button>
    </Dialog.Trigger>
    <Dialog.Content css={{ display: 'grid', gap: 'space.300', p: 'space.400' }} size="large">
      <Dialog.Title data-samba-component={true}>Goal settings</Dialog.Title>
      <ErrorBoundary fallbackType="partial">
        <SettingsModalView />
      </ErrorBoundary>
    </Dialog.Content>
  </Dialog>
);
